// PortfolioSummaryBar — the Portfolio page's top hero/summary stats bar: the
// portfolio balance on the left (with a gradient accent bar, fund count and the
// vested chip — dollars and percentage, as on the Dashboard hero), and four
// return-metric cards on the right (Today / MTD / YTD /
// 1-year). Source of truth: portfolio3.jsx .pf3-hero--top. Pure-presentational
// (data in via props); reuses the landing balance treatment via <Money>.
//
// Pending balances (FM-2098): the normalized `balance` model from the portfolio
// adapter drives two independent treatments, either/both of which can show:
//   • pending money (pending !== 0) → the same caption the Dashboard hero uses —
//     a "PENDING ACTIVITY · $X in your balance" row beside the headline. FM-2116
//     semantics: the headline ALREADY INCLUDES that money, which is exactly what the
//     caption says ("in your balance") — so both heroes state one figure for one
//     participant, and the caption breaks out how much of it is still in flight;
//   • a net-zero in-flight transaction (an item with amount === null, e.g. a
//     rebalance) → a neutral processing note under the balance.
// With no pending items nothing extra renders (no stray element, no gap).
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Surface, Eyebrow, Money, AsOfPill, Skeleton, Tooltip } from '../../../reusable';
import { CheckIcon } from '../../../icons/Icons';
import { formatCurrency } from '../../../../utils/format';
import { ReturnMetricCard } from './ReturnMetricCard';
import './portfolio-summary-bar.css';

// The four return windows, in display order. Today shows 2 decimals + the
// up-trend glyph (matching the design); the rest show 1 decimal. Labels resolve
// via i18n at render (labelKey/shortKey → translation.json portfolio.summary.*).
const METRICS = [
  { key: 'today', labelKey: 'today', shortKey: 'today', digits: 2, showArrow: true },
  { key: 'mtd', labelKey: 'mtd', shortKey: 'mtd', digits: 1 },
  { key: 'ytd', labelKey: 'ytd', shortKey: 'ytdShort', digits: 1 },
  { key: 'oneYr', labelKey: 'oneYr', shortKey: 'oneYrShort', digits: 1 },
];

export function PortfolioSummaryBar({
  summary,
  loading = false,
  asOf,
  balance = null,
  headlineBasis = 'total',
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <Surface className="portfolio-summary-bar" padding="none">
        <div className="portfolio-summary-bar__row" aria-busy="true">
          <div className="portfolio-summary-bar__bal">
            <Skeleton variant="text" width={110} />
            <Skeleton variant="title" width={220} />
            <Skeleton variant="text" width={160} />
          </div>
          <div className="portfolio-summary-bar__cells">
            {METRICS.map((m) => (
              <div key={m.key} className="return-metric-card">
                <Skeleton variant="text" width={56} />
                <Skeleton variant="text" width={72} />
              </div>
            ))}
          </div>
        </div>
      </Surface>
    );
  }

  const { fundCount, vestedPct, vestedBalance, returns } = summary;

  // `balance` is the FM-2098 model; callers that predate it (and the loading
  // baseline) still get the plain settled figure off `summary.balance`.
  const pendingAmount = balance?.pending ?? 0;
  // An outgoing (net-negative) pending item — e.g. an in-flight distribution or
  // transfer — is money on its way out, and is worth saying just as much as money
  // on its way in; gating on `> 0` would silently drop it. Gate on `!== 0`, then
  // pick directional copy.
  const hasPendingAmount = pendingAmount !== 0;
  const pendingIsOutflow = pendingAmount < 0;
  // Net-zero in-flight items — no dollar figure, so they only get a neutral note.
  const processingItems = (balance?.pendingItems || []).filter((i) => i.amount == null);
  const headlineValue = balance
    ? (headlineBasis === 'settled' ? balance.settled : balance.total)
    : summary.balance;

  // Cents kept, matching the Dashboard hero's caption ("$992.50 in your balance").
  // Always the magnitude — direction is conveyed by the copy, not a leading minus.
  const pendingText = formatCurrency(Math.abs(pendingAmount));

  // At 100% the dollar figure is just the balance restated one line above it, so
  // the chip says "100% vested" and stops — the amount only earns its place when
  // some of the balance is NOT yours yet, which is the whole question vesting
  // answers. Same rule and threshold as the Dashboard hero (`isFullyVested`), so
  // one participant can't read as fully vested on one screen and itemized on the
  // other. Below 100% the dollar figure still needs the plan to report vesting;
  // without it the chip falls back to the bare percentage rather than "$— vested".
  const isFullyVested = vestedPct >= 100;
  const vestedLabel = (isFullyVested || vestedBalance == null)
    ? t('portfolio.summary.vested', { pct: vestedPct })
    : t('portfolio.summary.vestedAmount', { amount: formatCurrency(vestedBalance, false), pct: vestedPct });

  return (
    <Surface className="portfolio-summary-bar" padding="none">
      <div className="portfolio-summary-bar__row">
        {/* Left — balance */}
        <div className="portfolio-summary-bar__bal">
          <div className="portfolio-summary-bar__head">
            <Eyebrow>{t('portfolio.summary.eyebrow')}</Eyebrow>
            <AsOfPill date={balance?.asOf || asOf} />
          </div>
          <div className="portfolio-summary-bar__amount">
            <Money value={headlineValue} size="hero" splitCents />
            {/* Money received but not yet posted. The whole row goes when there's
                nothing pending — "$0.00 in your balance" is noise, not an empty
                state. Same caption, same wording as the Dashboard hero. */}
            {hasPendingAmount && (
              <div className="portfolio-summary-bar__pending">
                <span className="portfolio-summary-bar__pending-eyebrow">
                  {t('portfolio.summary.pendingActivity')}
                </span>
                <span className="portfolio-summary-bar__pending-amount">
                  {t(
                    pendingIsOutflow
                      ? 'portfolio.summary.pendingLeavingBalance'
                      : 'portfolio.summary.pendingInBalance',
                    { amount: pendingText },
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Funds · vested stays the first line under the balance; any processing
              note(s) stack below it. */}
          <div className="portfolio-summary-bar__sub">
            <span>{t('portfolio.summary.funds', { count: fundCount })}</span>
            {vestedPct != null && (
              <>
                <span className="portfolio-summary-bar__dot" aria-hidden="true" />
                <Tooltip
                  className="portfolio-summary-bar__vested"
                  content={<>{t('portfolio.summary.vestedTip')}<br />{t('portfolio.summary.unvestedTip')}</>}
                >
                  <CheckIcon aria-hidden="true" /> {vestedLabel}
                </Tooltip>
              </>
            )}
          </div>

          {processingItems.map((item) => {
            // The label is the data part of the sentence; a row that arrives
            // without one still reads correctly rather than starting mid-sentence.
            // A rebalance ships no backend label — its localized label is built here
            // from the fund count via i18n, so no English chrome is synthesized in
            // the adapter (CLAUDE.md §16).
            let label = item.label;
            if (!label) {
              if (item.type === 'rebalance') {
                label = item.count
                  ? t('portfolio.summary.rebalanceLabel', { count: item.count })
                  : t('portfolio.summary.rebalanceLabelNoCount');
              } else {
                label = t('portfolio.summary.processingFallbackLabel');
              }
            }
            return (
              <p
                key={`${item.type}-${label}-${item.date || ''}`}
                className="portfolio-summary-bar__processing-note"
              >
                {item.date
                  ? t('portfolio.summary.processingNote', { label, date: item.date })
                  : t('portfolio.summary.processingNoteNoDate', { label })}
              </p>
            );
          })}
        </div>

        {/* Right — return cells */}
        <div className="portfolio-summary-bar__cells">
          {METRICS.map((m) => (
            <ReturnMetricCard
              key={m.key}
              label={t(`portfolio.summary.${m.labelKey}`)}
              shortLabel={t(`portfolio.summary.${m.shortKey}`)}
              pct={returns[m.key].pct}
              amount={returns[m.key].amount}
              digits={m.digits}
              showArrow={m.showArrow}
            />
          ))}
        </div>
      </div>
    </Surface>
  );
}

const returnShape = PropTypes.shape({
  pct: PropTypes.number,
  amount: PropTypes.number,
});

const dateShape = PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]);

// One not-yet-settled transaction. `amount` is null for net-zero movements (a
// rebalance shifts units between funds without changing the balance).
const pendingItemShape = PropTypes.shape({
  type: PropTypes.oneOf([
    'credit',
    'contribution',
    'trade',
    'rebalance',
    'transfer',
    'distribution',
  ]),
  label: PropTypes.string,
  // Present for a rebalance (net-zero) item; the component localizes the label
  // from it rather than the adapter shipping an English string.
  count: PropTypes.number,
  amount: PropTypes.number,
  status: PropTypes.string,
  date: PropTypes.string,
});

PortfolioSummaryBar.propTypes = {
  summary: PropTypes.shape({
    balance: PropTypes.number.isRequired,
    fundCount: PropTypes.number.isRequired,
    vestedPct: PropTypes.number,
    /** Dollar value of the vested share. Omit to show the percentage alone. */
    vestedBalance: PropTypes.number,
    returns: PropTypes.shape({
      today: returnShape,
      mtd: returnShape,
      ytd: returnShape,
      oneYr: returnShape,
    }).isRequired,
  }),
  loading: PropTypes.bool,
  asOf: dateShape,
  /**
   * Normalized balance model from the portfolio adapter (FM-2098). Omit to fall
   * back to the plain `summary.balance` headline with no pending treatment.
   */
  balance: PropTypes.shape({
    /** settled + pending. */
    total: PropTypes.number,
    /** Invested balance — what the fund positions add up to. */
    settled: PropTypes.number,
    /** Net of every pending item that carries an amount (0 when none do). */
    pending: PropTypes.number,
    asOf: dateShape,
    pendingItems: PropTypes.arrayOf(pendingItemShape),
  }),
  /**
   * Which figure the headline shows. 'total' (default) = the account balance, which
   * under FM-2116 already includes in-flight money — the Dashboard hero's treatment.
   * 'settled' shows the invested figure with the pending carved out instead.
   */
  headlineBasis: PropTypes.oneOf(['total', 'settled']),
};

export default PortfolioSummaryBar;
